/**
 * @file 見取り図 — 単元 × ステップの表で、入園前から年長までのロードマップを 1 枚で見る面。
 *
 * @what: 行 = 単元 (領域ごとにまとめる)、列 = 4 つのステップ。マスに、そのステップで目指す
 *   水準 (触れる → 導入 → 練習 → 仕上げ) と、そのステップ・その単元で使う教材を登録順に
 *   並べる (教材が無ければ「教材はまだありません」)。表の上には、ステップごとの大切にする
 *   こと・終わりまでにできていてほしいこと (関係する単元の出題数つき)・確認と場の点検・
 *   用意するものを置く。お子さんを選んでいれば、行の先頭で単元の状態 (未着手 → 取り組み中
 *   → できた) を切り替えられ、いまのステップの列に印が付く。表の上に「次に何をするか」
 *   (取り組み中 / 教材が決まっていない単元 / 教材を使い終えた単元) を出す。
 * @why: 「各単元の箇所にやるべき教材が並んでいる状態」が理想で、水準の一般的な説明文は
 *   マスに出しても役に立たない (代表 2026-08-23)。マスは水準と教材だけにする。
 *   列は 4 つを画面幅に詰め込まず、1 列ごとに読める幅を取って表の中で横にスクロールする。
 *   いまのステップの列は他より広く、開いたときにその列が見える位置まで送る (見る比重が
 *   いまのステップに寄るため。代表 2026-08-23)。表の上の「列へ移動」で他の列へ送れる。
 * @failure: 描画のみ。お子さんの情報が無ければ状態・教材・次の候補を出さず、表だけを出す。
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import {
  STUDY_MATERIAL_STATUS_LABELS,
  STUDY_UNIT_STATUS_LABELS,
  type StudyMaterialInput,
  type StudyProgressLog,
  type StudyProgressLogInput,
  type StudyUnitState,
  type StudyUnitStatus,
} from "../api/plans";
import { levelById, type AttainmentModel } from "../model";
import { formatLogDate } from "../api/dates";
import { entrySummary, type CorpusEntry } from "../model/corpusIndex";
import {
  cellStageFor,
  checkMark,
  checkpointsForStage,
  isTimedCheck,
  nextSteps,
} from "../model/milestones";
import { domainColor } from "./domainColors";
import { FocusList } from "./FocusList";
import { MaterialEditor } from "./MaterialEditor";
import { MaterialCover } from "./MaterialsPanel";
import { NO_SHELF_BOOKS, type ShelfBooks } from "../model/shelfBooks";
import type { RoadmapMaterial } from "./roadmapTypes";
import { StudyDialog } from "./StudyDialog";
import { UnitDialog } from "./UnitDialog";
import { RoadmapNextList } from "./RoadmapNext";
import { RoadmapStageTrack } from "./RoadmapStageTrack";
import { ASSESSMENT_ICONS, DOMAIN_ICONS, PREPARATION_KIND_ICONS } from "./studyIcons";
import "./study.css";

export type { RoadmapMaterial } from "./roadmapTypes";

/**
 * 出題例モーダルで単元ごとに出す問数の上限。
 * @why: モーダルはその場の下見用で、スクロールせず読み切れる量に抑える (8 = 図の説明 1 行 × 8 で
 *   1 画面弱)。全部見たいときは「すべて見る」でリーダーの単元しぼりこみへ送る。
 */
const EXIT_CORPUS_PREVIEW = 8;

export interface MilestonePanelProps {
  model: AttainmentModel;
  /** お子さんの「いまここ」のステップ。無ければ印を付けない。 */
  currentStageId?: string;
  /** お子さんの単元の状態。無ければ状態を出さない。 */
  unitStates?: Readonly<Record<string, { status: StudyUnitStatus; note?: string }>>;
  /** 指定されると行の先頭の状態ボタンで切り替えられる。 */
  onToggleUnit?: (unitId: string) => void;
  /** 指定されるとステップの見出しに「このステップをいまここにする」を出す。 */
  onMakeCurrent?: (stageId: string) => void;
  prepStates?: Readonly<Record<string, boolean>>;
  onTogglePrep?: (prepId: string) => void;
  /** 「終わりまでにできていてほしいこと」の達成チェック (draft)。指定されるとチェックリストになる。 */
  exitStates?: Readonly<Record<string, boolean>>;
  onToggleExit?: (exitId: string) => void;
  /** サーバが持つ達成状態 (達成日)。 */
  serverExitStates?: Readonly<Record<string, { achieved_at: string }>>;
  /** unit id → 紐づけた教材。stage_id があればそのステップのマスに、無ければ行の先頭に出す。 */
  materialsByUnit?: Readonly<Record<string, readonly RoadmapMaterial[]>>;
  /** stage id → 単元を決めずにステップだけ決めた教材 (ステップ全体で使うもの)。 */
  materialsByStage?: Readonly<Record<string, readonly RoadmapMaterial[]>>;
  /** unit id → 取り込んだ資料での出題数。終わりまでにできていてほしいことの横に出す。 */
  questionCountByUnit?: Readonly<Record<string, number>>;
  learnerName?: string;
  /** draft の教材 (教材の対話で編集する)。 */
  materials?: StudyMaterialInput[];
  onChangeMaterials?: (next: StudyMaterialInput[]) => void;
  /** 取り組みの記録 (単元・教材の対話に日付つきで出す)。 */
  logs?: StudyProgressLog[];
  onAddLog?: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  logBusy?: boolean;
  logError?: string | null;
  /** サーバが持つ単元の状態 (始めた日・できた日)。 */
  serverUnitStates?: Readonly<Record<string, StudyUnitState>>;
  /** unit id → 取り込んだ入試問題 (単元の対話から読む)。 */
  corpusByUnit?: Readonly<Record<string, CorpusEntry[]>>;
  sourceHrefBase?: string;
  /** 本だなにある本の判定を渡す。教材の表紙の左下に印を出すのに使う。 */
  shelf?: ShelfBooks;
  /** 「教材を探す」で教材タブを開く。 */
  onOpenMaterialsTab?: () => void;
}

/** 領域の色を CSS 変数で渡す (カスタムプロパティは CSSProperties に無いのでここだけ型を広げる)。 */
function domainStyle(color: string): CSSProperties {
  return { "--study-domain-color": color } as CSSProperties;
}

function MaterialChip({
  material,
  onOpen,
  muted,
}: {
  material: RoadmapMaterial;
  onOpen?: (id: string) => void;
  muted?: boolean;
}) {
  const cls = `study-material-chip study-material-chip--${material.status}${muted ? " study-material-chip--muted" : ""}`;
  if (onOpen) {
    return (
      <button
        type="button"
        className={`${cls} study-material-chip--btn`}
        title={`${STUDY_MATERIAL_STATUS_LABELS[material.status]}。タップで詳細`}
        onClick={() => onOpen(material.id)}
      >
        {material.title}
      </button>
    );
  }
  return (
    <span className={cls} title={STUDY_MATERIAL_STATUS_LABELS[material.status]}>
      {material.title}
    </span>
  );
}

export function MilestonePanel({
  model,
  currentStageId,
  unitStates,
  onToggleUnit,
  onMakeCurrent,
  prepStates,
  onTogglePrep,
  exitStates,
  onToggleExit,
  serverExitStates,
  materialsByUnit,
  materialsByStage,
  questionCountByUnit,
  learnerName,
  materials,
  onChangeMaterials,
  logs,
  onAddLog,
  logBusy,
  logError,
  serverUnitStates,
  corpusByUnit,
  sourceHrefBase,
  shelf = NO_SHELF_BOOKS,
  onOpenMaterialsTab,
}: MilestonePanelProps) {
  const [openUnitId, setOpenUnitId] = useState<string | null>(null);
  const [openMaterialId, setOpenMaterialId] = useState<string | null>(null);
  // 「いまここ」の変更はダイアログから (年に 1 回の操作なので、列見出しに大きいボタンを並べない。
  // onMakeCurrent が無い読取専用表示ではリンク自体を出さない)
  const [stagePickerOpen, setStagePickerOpen] = useState(false);
  // 「終わりまでにできていてほしいこと」の出題例。corpusByUnit を読めているときだけ数字をボタンに
  // するので、ここが開くとき出題データは必ずある (未読込時は従来どおり数字だけ)
  const [openExit, setOpenExit] = useState<{ text: string; units: readonly string[] } | null>(null);
  const openUnit = openUnitId ? (model.units.find((u) => u.id === openUnitId) ?? null) : null;
  const openMaterial = openMaterialId
    ? (materials?.find((m) => m.id === openMaterialId) ?? null)
    : null;
  const canOpenMaterial = materials !== undefined && onChangeMaterials !== undefined;
  // 単元の対話から教材を開くときは、単元の対話を閉じる (対話を重ねない)
  const openMaterialDialog = (id: string) => {
    setOpenMaterialId(id);
    setOpenUnitId(null);
  };
  const stageIndex = (id: string) => model.stages.findIndex((s) => s.id === id);
  const editable = unitStates !== undefined && onToggleUnit !== undefined;
  const colClass = (stageId: string, base: string) =>
    `${base}${stageId === currentStageId ? ` ${base}--current` : ""}`;
  const checkpoints = useMemo(
    () => model.stages.map((s) => checkpointsForStage(model, s.id)),
    [model],
  );
  const unitLabelById = useMemo(() => new Map(model.units.map((u) => [u.id, u.label])), [model]);
  const next = useMemo(
    () =>
      currentStageId && unitStates
        ? nextSteps(model, currentStageId, unitStates, materialsByUnit ?? {})
        : null,
    [model, currentStageId, unitStates, materialsByUnit],
  );
  const currentStage = model.stages.find((s) => s.id === currentStageId) ?? null;
  const hasStageMaterials =
    materialsByStage !== undefined && Object.values(materialsByStage).some((l) => l.length > 0);

  // 横スクロールの器。列見出し (th) の位置から、その列が単元名の列のすぐ右に来るように送る。
  const scrollRef = useRef<HTMLDivElement>(null);
  // scrollLeft への代入で送る (smooth な scrollTo は環境によって動かないことがあり、瞬時に
  // 送るほうが確実)。位置は器とのビューポート基準の差で取り、offsetParent の都合に依らない。
  const scrollToStage = (stageId: string) => {
    const box = scrollRef.current;
    if (!box) return;
    const head = box.querySelector<HTMLElement>(`th[data-stage-id="${stageId}"]`);
    const corner = box.querySelector<HTMLElement>("th.study-roadmap-corner");
    if (!head) return;
    const headLeft =
      head.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
    const cornerWidth = corner?.getBoundingClientRect().width ?? 0;
    box.scrollLeft = Math.max(0, headLeft - cornerWidth);
  };
  // 開いたとき (と いまここ が変わったとき) は、いまのステップの列が見える位置から始める。
  useEffect(() => {
    if (currentStageId) scrollToStage(currentStageId);
  }, [currentStageId, model]);

  return (
    <div className="study-roadmap">
      {next && currentStage ? (
        <section className="study-roadmap-now" aria-label="次に何をするか">
          <h3 className="study-roadmap-now-title">
            {learnerName
              ? `${learnerName}はいま ${currentStage.period}`
              : `いま ${currentStage.period}`}
            <span className="study-roadmap-now-sub">（{currentStage.label}）</span>
          </h3>
          <div className="study-roadmap-now-groups">
            <RoadmapNextList
              title="取り組み中"
              units={next.inProgress}
              empty="なし（表の行の先頭で「取り組み中」にすると並びます）"
            />
            <RoadmapNextList
              title="教材がまだ決まっていない単元"
              units={next.noMaterial}
              empty="なし（すべて決まっています）"
            />
            <RoadmapNextList
              title="教材を使い終えた単元（次の 1 冊を考える）"
              units={next.materialFinished}
              empty="なし"
            />
          </div>
        </section>
      ) : null}

      <div className="study-roadmap-jump" role="group" aria-label="列へ移動">
        <span className="study-roadmap-jump-label">列へ移動</span>
        {model.stages.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`study-roadmap-jump-btn${
              s.id === currentStageId ? " study-roadmap-jump-btn--current" : ""
            }`}
            onClick={() => scrollToStage(s.id)}
          >
            {s.period}
            {s.id === currentStageId ? (
              <span className="study-visually-hidden">（いまここ）</span>
            ) : null}
          </button>
        ))}
        {onMakeCurrent ? (
          <button
            type="button"
            className="study-link-btn study-roadmap-stage-change"
            onClick={() => setStagePickerOpen(true)}
          >
            ステップを変更する
          </button>
        ) : null}
      </div>
      <ul className="study-map-legend" aria-label="水準の意味">
        {model.levels.map((l) => (
          <li key={l.id}>
            <span className={`study-level study-level--${l.id}`}>{l.label}</span>
            <span className="study-map-legend-desc">{l.description}</span>
          </li>
        ))}
      </ul>
      <div className="study-roadmap-scroll" ref={scrollRef}>
        <table className="study-roadmap-table">
          <colgroup>
            <col className="study-roadmap-col-head" />
            {model.stages.map((s) => (
              <col
                key={s.id}
                className={`study-roadmap-col${
                  s.id === currentStageId ? " study-roadmap-col--current" : ""
                }`}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col" className="study-roadmap-corner">
                単元
              </th>
              {model.stages.map((s) => (
                <th
                  key={s.id}
                  scope="col"
                  data-stage-id={s.id}
                  className={colClass(s.id, "study-roadmap-head")}
                >
                  <span className="study-roadmap-head-period">{s.period}</span>
                  <span className="study-roadmap-head-sub">
                    {s.label}
                    {s.id === currentStageId ? (
                      <span className="study-now-badge">
                        {learnerName ? `${learnerName}はいまここ` : "いまここ"}
                      </span>
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="study-roadmap-overview">
            <tr>
              <th scope="row" className="study-roadmap-rowhead">
                大切にすること
              </th>
              {model.stages.map((s) => (
                <td key={s.id} className={colClass(s.id, "study-roadmap-cell")}>
                  <FocusList focus={s.focus} />
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" className="study-roadmap-rowhead">
                終わりまでにできていてほしいこと
                {questionCountByUnit ? (
                  <span className="study-map-rowhead-sub">
                    「出題」は取り込んだ入試問題での、関係する単元の出題数
                  </span>
                ) : null}
              </th>
              {model.stages.map((s) => (
                <td key={s.id} className={colClass(s.id, "study-roadmap-cell")}>
                  <ul className={`study-map-goals${exitStates ? " study-map-goals--check" : ""}`}>
                    {s.exit.map((e) => {
                      const count = questionCountByUnit
                        ? e.units.reduce((n, id) => n + (questionCountByUnit[id] ?? 0), 0)
                        : 0;
                      const achievedAt = serverExitStates?.[e.id]?.achieved_at;
                      return (
                        <li key={e.id}>
                          {exitStates && onToggleExit ? (
                            <label className="study-exit-check">
                              <input
                                type="checkbox"
                                checked={exitStates[e.id] === true}
                                onChange={() => onToggleExit(e.id)}
                              />
                              <span
                                className={
                                  exitStates[e.id] === true ? "study-exit-check--done" : undefined
                                }
                              >
                                {e.text}
                              </span>
                            </label>
                          ) : (
                            e.text
                          )}
                          {exitStates?.[e.id] === true && achievedAt ? (
                            <span className="study-exit-achieved">
                              {formatLogDate(achievedAt.slice(0, 10))} 達成
                            </span>
                          ) : null}
                          {count > 0 ? (
                            corpusByUnit ? (
                              <button
                                type="button"
                                className="study-roadmap-exit-count study-roadmap-exit-count--btn"
                                title={`${e.units
                                  .map((id) => unitLabelById.get(id) ?? id)
                                  .join("、")}。タップで出題例`}
                                onClick={() => setOpenExit({ text: e.text, units: e.units })}
                              >
                                出題 {count} 問
                              </button>
                            ) : (
                              <span
                                className="study-roadmap-exit-count"
                                title={e.units.map((id) => unitLabelById.get(id) ?? id).join("、")}
                              >
                                出題 {count} 問
                              </span>
                            )
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" className="study-roadmap-rowhead">
                チェックテストと体験のふり返り
                <span className="study-map-rowhead-sub">
                  初めて見る問題で解ける割合と、体験の機会が足りているか
                </span>
              </th>
              {model.stages.map((s, si) => {
                const cps = checkpoints[si]!;
                return (
                  <td key={s.id} className={colClass(s.id, "study-roadmap-cell")}>
                    {cps.length > 0 ? (
                      <>
                        <RoadmapStageTrack stage={s} checkpoints={cps} />
                        <ul className="study-map-checks">
                          {cps.map((c) => (
                            <li key={c.id}>
                              <span className="study-map-check-name">
                                {isTimedCheck(c) ? `${checkMark(c)} ` : ""}
                                {c.label}
                              </span>
                              <span className="study-map-check-when">{c.timing}</span>
                              <span className="study-map-check-crit">{c.criterion}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="study-map-empty">このステップでは測りません</p>
                    )}
                  </td>
                );
              })}
            </tr>
            {hasStageMaterials ? (
              <tr>
                <th scope="row" className="study-roadmap-rowhead">
                  毎日の幹になる教材
                  <span className="study-map-rowhead-sub">
                    単元を決めずに登録した教材。進め方はメモから
                  </span>
                </th>
                {model.stages.map((s) => (
                  <td key={s.id} className={colClass(s.id, "study-roadmap-cell")}>
                    <ul className="study-roadmap-cell-materials study-roadmap-cell-materials--stage">
                      {(materialsByStage?.[s.id] ?? []).map((m) => (
                        <li key={m.id}>
                          <MaterialChip
                            material={m}
                            {...(canOpenMaterial ? { onOpen: openMaterialDialog } : {})}
                          />
                          {m.memo ? (
                            <span className="study-roadmap-stage-memo">{m.memo}</span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </td>
                ))}
              </tr>
            ) : null}
            <tr>
              <th scope="row" className="study-roadmap-rowhead">
                用意するもの
                {prepStates && onTogglePrep ? (
                  <span className="study-map-rowhead-sub">そろったものにチェックを入れます</span>
                ) : null}
              </th>
              {model.stages.map((s) => {
                const renderPrep = (p: (typeof s.preparations)[number]) => (
                  <li key={p.id}>
                    {prepStates && onTogglePrep ? (
                      <label className="study-roadmap-prep">
                        <input
                          type="checkbox"
                          checked={prepStates[p.id] === true}
                          onChange={() => onTogglePrep(p.id)}
                        />
                        <span aria-hidden="true">{PREPARATION_KIND_ICONS[p.kind]}</span>
                        <span>{p.label}</span>
                      </label>
                    ) : (
                      <span className="study-roadmap-prep">
                        <span aria-hidden="true">{PREPARATION_KIND_ICONS[p.kind]}</span>
                        <span>{p.label}</span>
                      </span>
                    )}
                  </li>
                );
                // そろったものは「購入済み」にたたむ (まだのものが先に目に入るように)
                const done = prepStates
                  ? s.preparations.filter((p) => prepStates[p.id] === true)
                  : [];
                const todo = prepStates
                  ? s.preparations.filter((p) => prepStates[p.id] !== true)
                  : s.preparations;
                return (
                  <td key={s.id} className={colClass(s.id, "study-roadmap-cell")}>
                    {todo.length > 0 ? (
                      <ul className="study-roadmap-preps">{todo.map(renderPrep)}</ul>
                    ) : prepStates ? (
                      <p className="study-map-empty">すべてそろっています</p>
                    ) : null}
                    {done.length > 0 ? (
                      <details className="study-prep-done">
                        <summary>購入済み {done.length} 件</summary>
                        <ul className="study-roadmap-preps">{done.map(renderPrep)}</ul>
                      </details>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          </tbody>

          {model.domains.map((d) => {
            const units = model.units.filter((u) => u.domain === d.id);
            if (units.length === 0) return null;
            const color = domainColor(d.id);
            return (
              <tbody
                key={d.id}
                className="study-roadmap-domain"
                style={color ? domainStyle(color) : undefined}
              >
                <tr className="study-roadmap-domain-row">
                  <th scope="rowgroup" colSpan={model.stages.length + 1}>
                    <span className="study-roadmap-domain-sticky">
                      <span className="study-domain-dot" aria-hidden="true" />
                      {DOMAIN_ICONS[d.id] ? (
                        <span aria-hidden="true">{DOMAIN_ICONS[d.id]}</span>
                      ) : null}
                      <span className="study-roadmap-domain-label">{d.label}</span>
                      <span className="study-roadmap-domain-aim">{d.aim}</span>
                    </span>
                  </th>
                </tr>
                {units.map((u) => {
                  const state = unitStates?.[u.id];
                  const status: StudyUnitStatus = state?.status ?? "not_started";
                  const statusLabel = STUDY_UNIT_STATUS_LABELS[status];
                  const unitMaterials = materialsByUnit?.[u.id] ?? [];
                  // ステップを決めていない教材だけ行の先頭に出す (決めてあればそのマスに出る)
                  const looseMaterials = unitMaterials.filter((m) => !m.stage_id);
                  return (
                    <tr key={u.id} className={`study-roadmap-unit study-roadmap-unit--${status}`}>
                      <th scope="row" className="study-roadmap-unit-head">
                        {/* th 自体は table-cell のまま (display: grid にすると行の高さに伸びず、固定列の背景が
                            途中で切れてスクロール中の中身が透ける)。並べ方は内側の div が持つ */}
                        <div className="study-roadmap-unit-head-inner">
                          <div className="study-roadmap-unit-label">
                            <button
                              type="button"
                              className="study-roadmap-unit-name"
                              onClick={() => setOpenUnitId(u.id)}
                              title="タップで単元の詳細（状態・記録・教材・入試の出題）"
                            >
                              {u.label}
                            </button>
                            {u.assessments.map((a) => {
                              const kind = model.assessments.find((x) => x.id === a);
                              return (
                                <span
                                  key={a}
                                  className="study-assessment-icon"
                                  title={kind?.label ?? a}
                                  aria-hidden="true"
                                >
                                  {ASSESSMENT_ICONS[a] ?? "・"}
                                </span>
                              );
                            })}
                          </div>
                          {unitStates ? (
                            editable ? (
                              <button
                                type="button"
                                className={`study-status-btn study-status-btn--${status}`}
                                onClick={() => onToggleUnit(u.id)}
                                aria-label={`${u.label}（${statusLabel}）`}
                                title="クリックで 未着手 → 取り組み中 → できた の順に切り替わります"
                              >
                                {statusLabel}
                              </button>
                            ) : (
                              <span className={`study-status-btn study-status-btn--${status}`}>
                                {statusLabel}
                              </span>
                            )
                          ) : null}
                          {looseMaterials.length > 0 ? (
                            <div className="study-unit-materials">
                              {looseMaterials.map((mat) => (
                                <MaterialChip
                                  key={mat.id}
                                  material={mat}
                                  {...(canOpenMaterial ? { onOpen: openMaterialDialog } : {})}
                                />
                              ))}
                            </div>
                          ) : null}
                          {state?.note ? (
                            <div className="study-unit-memo">メモ: {state.note}</div>
                          ) : null}
                        </div>
                      </th>
                      {model.stages.map((s) => {
                        const levelId = u.expected[s.id];
                        const level = levelId ? levelById(model, levelId) : null;
                        const note = u.notes[s.id];
                        // 使う時期がこの列の教材。単元がその時期にまだ無い教材 (早めに始めるもの) は、
                        // その時期以降で最初にこの単元が現れる列に出す
                        const cellMaterials = unitMaterials.filter(
                          (m) =>
                            m.stage_id !== undefined && cellStageFor(model, u, m.stage_id) === s.id,
                        );
                        return (
                          <td
                            key={s.id}
                            className={`${colClass(s.id, "study-roadmap-cell")}${
                              level
                                ? ` study-roadmap-cell--${level.id}`
                                : " study-roadmap-cell--none"
                            }`}
                          >
                            {level ? (
                              <>
                                <span
                                  className={`study-level study-level--${level.id}`}
                                  title={level.description}
                                >
                                  {level.label}
                                </span>
                                {cellMaterials.length > 0 ? (
                                  <ul className="study-roadmap-cell-materials">
                                    {cellMaterials.map((m) => (
                                      <li key={m.id}>
                                        <MaterialChip
                                          material={m}
                                          {...(canOpenMaterial
                                            ? { onOpen: openMaterialDialog }
                                            : {})}
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                ) : level.id === "experience" ? (
                                  <span className="study-roadmap-cell-empty">
                                    {level.description}
                                  </span>
                                ) : unitStates ? (
                                  <CellFallback
                                    stageWide={materialsByStage?.[s.id] ?? []}
                                    earlier={unitMaterials.filter(
                                      (m) =>
                                        m.stage_id !== undefined &&
                                        stageIndex(m.stage_id) < stageIndex(s.id),
                                    )}
                                    {...(canOpenMaterial ? { onOpen: openMaterialDialog } : {})}
                                    {...(onOpenMaterialsTab ? { onOpenMaterialsTab } : {})}
                                  />
                                ) : null}
                                {note ? <span className="study-roadmap-note">{note}</span> : null}
                              </>
                            ) : (
                              <span className="study-visually-hidden">
                                このステップでは扱わない
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      </div>

      {openUnit ? (
        <UnitDialog
          model={model}
          unit={openUnit}
          onClose={() => setOpenUnitId(null)}
          {...(unitStates ? { status: unitStates[openUnit.id]?.status ?? "not_started" } : {})}
          {...(onToggleUnit ? { onToggleStatus: () => onToggleUnit(openUnit.id) } : {})}
          {...(serverUnitStates?.[openUnit.id]
            ? { serverState: serverUnitStates[openUnit.id]! }
            : {})}
          logs={(logs ?? []).filter((l) => l.unit_id === openUnit.id && l.memo)}
          {...(onAddLog ? { onAddLog } : {})}
          logBusy={logBusy === true}
          logError={logError ?? null}
          materials={materialsByUnit?.[openUnit.id] ?? []}
          {...(canOpenMaterial ? { onOpenMaterial: openMaterialDialog } : {})}
          corpusEntries={corpusByUnit?.[openUnit.id] ?? []}
          {...(sourceHrefBase ? { sourceHrefBase } : {})}
        />
      ) : null}

      {stagePickerOpen && onMakeCurrent ? (
        <StudyDialog
          title="ステップを変更する"
          labelledBy="study-stage-picker-dialog"
          onClose={() => setStagePickerOpen(false)}
        >
          <p className="study-dialog-hint">
            {learnerName
              ? `${learnerName}のいまのステップを選びます。`
              : "いまのステップを選びます。"}
            表の印と「次に何をするか」がこのステップ基準になります。
          </p>
          <ul className="study-stage-picker">
            {model.stages.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`study-stage-picker-btn${
                    s.id === currentStageId ? " study-stage-picker-btn--current" : ""
                  }`}
                  disabled={s.id === currentStageId}
                  onClick={() => {
                    onMakeCurrent(s.id);
                    setStagePickerOpen(false);
                  }}
                >
                  <span className="study-stage-picker-period">{s.period}</span>
                  <span className="study-stage-picker-label">{s.label}</span>
                  {s.id === currentStageId ? (
                    <span className="study-now-badge">いまここ</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </StudyDialog>
      ) : null}

      {openExit && corpusByUnit ? (
        <StudyDialog
          title={`出題例 — ${openExit.text}`}
          labelledBy="study-exit-corpus-dialog"
          onClose={() => setOpenExit(null)}
        >
          <p className="study-dialog-hint">取り込んだ入試問題のうち、関係する単元の出題です。</p>
          {openExit.units.map((uid) => {
            const entries = corpusByUnit[uid] ?? [];
            if (entries.length === 0) return null;
            return (
              <section
                key={uid}
                className="study-dialog-section"
                aria-label={unitLabelById.get(uid) ?? uid}
              >
                <h4 className="study-dialog-h">
                  {unitLabelById.get(uid) ?? uid}（{entries.length} 問）
                </h4>
                <ul className="study-corpus-list">
                  {entries.slice(0, EXIT_CORPUS_PREVIEW).map((e) => (
                    <li key={`${e.sourceId}/${e.item.id}`} className="study-corpus-row">
                      <span className="study-corpus-school">
                        {e.item.index["出典校"] ?? "出典なし"}
                      </span>
                      <span className="study-corpus-summary">{entrySummary(e.item)}</span>
                      {sourceHrefBase ? (
                        <Link
                          className="study-corpus-open"
                          to={`${sourceHrefBase}/${encodeURIComponent(e.sourceId)}?item=${encodeURIComponent(e.item.id)}`}
                          onClick={() => setOpenExit(null)}
                        >
                          見る
                        </Link>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {entries.length > EXIT_CORPUS_PREVIEW && sourceHrefBase && entries[0] ? (
                  <Link
                    className="study-link-btn"
                    to={`${sourceHrefBase}/${encodeURIComponent(entries[0].sourceId)}?unit=${encodeURIComponent(uid)}`}
                    onClick={() => setOpenExit(null)}
                  >
                    {unitLabelById.get(uid) ?? uid} の出題をすべて見る（{entries.length} 問）
                  </Link>
                ) : null}
              </section>
            );
          })}
        </StudyDialog>
      ) : null}

      {openMaterial && materials && onChangeMaterials ? (
        <StudyDialog
          title={openMaterial.title}
          labelledBy={`study-material-dialog-${openMaterial.id}`}
          onClose={() => setOpenMaterialId(null)}
        >
          <div className="study-dialog-material">
            <MaterialCover
              isbn={openMaterial.isbn}
              url={openMaterial.url}
              title={openMaterial.title}
              onShelf={shelf.has(openMaterial.isbn)}
            />
            <div className="study-dialog-material-main">
              {openMaterial.publisher || openMaterial.isbn ? (
                <p className="study-material-book">
                  {openMaterial.publisher ? <span>{openMaterial.publisher}</span> : null}
                  {openMaterial.isbn ? <span>ISBN {openMaterial.isbn}</span> : null}
                </p>
              ) : null}
              <MaterialEditor
                model={model}
                material={openMaterial}
                onChange={(next) =>
                  onChangeMaterials(materials.map((m) => (m.id === next.id ? next : m)))
                }
                onRemove={() => {
                  onChangeMaterials(materials.filter((m) => m.id !== openMaterial.id));
                  setOpenMaterialId(null);
                }}
                pickerStage={
                  model.stages.find((st) => st.id === (openMaterial.stage_id || currentStageId)) ??
                  model.stages[0]!
                }
                logs={(logs ?? []).filter(
                  (l) => l.material_id === openMaterial.id && (l.memo || l.to_page),
                )}
                {...(onAddLog ? { onAddLog } : {})}
                logBusy={logBusy === true}
                logError={logError ?? null}
              />
            </div>
          </div>
        </StudyDialog>
      ) : null}
    </div>
  );
}

/**
 * 教材が無いマスに何を出すか (練習・仕上げで教材が無いと「意味不明」にならないように。代表 2026-08-24)。
 *   1. その時期の全体の教材 (単元を決めずに登録した 365日 など) があれば「全体の教材で進める」
 *   2. 前の時期にこの単元の教材があれば「前の時期の教材を続ける」+ その教材
 *   3. どちらも無ければ「教材を探す」(教材タブへ)
 */
function CellFallback({
  stageWide,
  earlier,
  onOpen,
  onOpenMaterialsTab,
}: {
  stageWide: readonly RoadmapMaterial[];
  earlier: readonly RoadmapMaterial[];
  onOpen?: (id: string) => void;
  onOpenMaterialsTab?: () => void;
}) {
  if (stageWide.length > 0) {
    return (
      <span className="study-roadmap-cell-hint" title={stageWide.map((m) => m.title).join(" / ")}>
        全体の教材で進める（{stageWide.length} 冊）
      </span>
    );
  }
  if (earlier.length > 0) {
    return (
      <span className="study-roadmap-cell-hint">
        前の時期の教材を続ける
        <span className="study-roadmap-cell-earlier">
          {earlier.slice(-3).map((m) => (
            <MaterialChip key={m.id} material={m} muted {...(onOpen ? { onOpen } : {})} />
          ))}
        </span>
      </span>
    );
  }
  if (onOpenMaterialsTab) {
    return (
      <button
        type="button"
        className="study-link-btn study-roadmap-cell-find"
        onClick={onOpenMaterialsTab}
      >
        教材を探す
      </button>
    );
  }
  return <span className="study-roadmap-cell-empty">教材はまだありません</span>;
}
